/* ==========================================================================
   Alwayz Clean Lab - search.js
   <predictive-search>: debounced predictive results (Section Rendering API),
   keyboard navigation, recent searches. Depends on global.js.
   ========================================================================== */
(() => {
  'use strict';
  const A = window.Alwayz;
  const { $, $$, define, debounce, fetchHTML, parseHTML } = A;
  const KEY = 'alwayz:recent-search';
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; } };
  const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, 6))); } catch (e) { /* noop */ } };
  const esc = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.enabled = this.dataset.enabled !== 'false';
      this.form = $('form', this);
      this.input = $('input[type="search"]', this);
      this.results = $('[data-search-results]', this);
      this.clearBtn = $('[data-search-clear]', this);
      this.recentWrap = $('[data-search-recent]', this);
      this.recentList = $('[data-search-recent-list]', this);
      this.cache = new Map();
      this.renderRecent();

      this.input.addEventListener('input', () => { this.selected = -1; $$('[aria-selected="true"]', this.results).forEach((el) => el.setAttribute('aria-selected', 'false')); });
      this.input.addEventListener('input', debounce(() => this.onInput(), 200));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.form.addEventListener('submit', () => this.remember(this.input.value));
      this.clearBtn && this.clearBtn.addEventListener('click', () => { this.input.value = ''; this.onInput(); this.input.focus(); });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-psearch-item], .psearch__link');
        if (link) this.remember(this.input.value);
      });
    }
    remember(term) {
      term = (term || '').trim();
      if (term.length < 2) return;
      write([term, ...read().filter((t) => t.toLowerCase() !== term.toLowerCase())]);
    }
    renderRecent() {
      const list = read();
      if (!this.recentWrap) return;
      this.recentWrap.hidden = !list.length;
      this.recentList.innerHTML = list.map((t) => `<a class="chip" href="${A.routes.search}?q=${encodeURIComponent(t)}">${esc(t)}</a>`).join('');
    }
    async onInput() {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = !q;
      if (q.length < 2 || !this.enabled) { this.setResults(''); return; }
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      this.results.innerHTML = '<div class="psearch__loading"><i></i><i></i><i></i></div>';
      this.classList.add('has-results');
      try {
        let html = this.cache.get(q);
        if (!html) {
          const params = new URLSearchParams({ q, 'resources[type]': 'product,collection,article,page,query', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last', section_id: 'predictive-search' });
          const res = await fetch(`${A.routes.predictiveSearch}?${params}`, { signal: this.abort.signal });
          if (!res.ok) throw new Error(res.statusText);
          html = await res.text();
          this.cache.set(q, html);
        }
        const doc = parseHTML(html);
        const inner = $('[data-psearch]', doc);
        this.setResults(inner ? inner.outerHTML : '');
      } catch (err) {
        if (err.name !== 'AbortError') this.setResults('');
      }
    }
    setResults(html) {
      this.results.innerHTML = html;
      this.classList.toggle('has-results', !!html);
      this.selected = -1;
    }
    items() { return $$('[data-psearch-item]', this.results); }
    onKey(e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const items = this.items();
      if (!items.length) return;
      if (e.key === 'Enter') {
        if (this.selected > -1) { e.preventDefault(); items[this.selected].click(); }
        return;
      }
      e.preventDefault();
      this.selected = (this.selected + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items.forEach((it, i) => it.setAttribute('aria-selected', String(i === this.selected)));
      items[this.selected].scrollIntoView({ block: 'nearest' });
    }
  }
  define('predictive-search', PredictiveSearch);
})();
